import { Tooltip } from 'antd';
import { SessionControlButton } from 'browser/components/Dashboard/VisitorFocused/SessionControls/SessionControlButton';
import clsx from 'clsx';
import React, { useEffect } from 'react';
import { Icon } from 'shared/components/Icon';
import { StatusBubble } from 'shared/components/StatusBubble';
import { VisitorAvatar } from 'shared/components/VisitorAvatar';
import { useAppDispatch, useAppSelector } from 'shared/store';
import { selectCallStatus, selectRealtimeVisitor } from 'shared/store/realtime-slice';
import CameraIcon from 'shared/assets/images/camera-icon.svg';
import { Button } from 'shared/components/Button';
import {
  activateVisitor,
  deactivateVisitor,
  startCall,
} from 'shared/store/dashboard-slice';
import { UniqueVisitor } from 'sb-lib/types';
import { ChatPopupActiveCall } from '../ActiveCall';
import './style.less';
import { usePrevious } from 'shared/hooks';

export const ChatPopupHeader: React.FC<{
  size: 'default' | 'collapsed';
  newUnreadMessages: number;
  setSize: React.Dispatch<React.SetStateAction<'default' | 'collapsed'>>;
  visitor: UniqueVisitor;
  conversationId: number;
  collapsedLine: string;
  firstLine: string;
  secondLine?: string | null;
  closeConversation: () => void;
}> = ({
  size,
  newUnreadMessages,
  setSize,
  visitor,
  conversationId,
  collapsedLine,
  firstLine,
  secondLine,
  closeConversation,
}) => {
  const dispatch = useAppDispatch();
  const activeVisitor = useAppSelector(state => selectRealtimeVisitor(state, visitor.id));
  const callStatus = useAppSelector(selectCallStatus);
  const { isReceivingAudio } = useAppSelector(s => s.realtime);
  const prevCallStatus = usePrevious(callStatus);

  const toggleCall = async (video = true) => {
    if (!visitor) return;
    await dispatch(activateVisitor({ visitorId: visitor.id }));
    dispatch(startCall({ activeConversationId: conversationId, video }));
  };

  useEffect(() => {
    if (
      ['active', 'establishing'].includes(prevCallStatus ?? '') &&
      ['off', 'rejected'].includes(callStatus)
    ) {
      dispatch(deactivateVisitor());
    }
  }, [callStatus, dispatch, prevCallStatus]);

  const menuItems = [
    {
      key: 'video-call',
      label: (
        <div className="CallButtons__menu-label">
          <div className="CallButtons__menu-label-inner">Video call</div>
          <Icon icon="chevronOn" width={24} height={24} />
        </div>
      ),
      icon: (
        <div className="CallButtons__menu-icon">
          <Icon icon="bubble" />
        </div>
      ),
      isDefault: true,
      onClick: toggleCall,
    },
    {
      key: 'audio-call',
      label: (
        <div className="CallButtons__menu-label">
          <div className="CallButtons__menu-label-inner">Audio call</div>
          <Icon icon="chevronOn" width={24} height={24} />
        </div>
      ),
      icon: (
        <div className="CallButtons__menu-icon">
          <Icon icon="phoneOn" />
        </div>
      ),
      onClick: () => toggleCall(false),
    },
  ];

  if (!visitor) return <></>;

  return (
    <>
      <div
        className={clsx(
          'ChatPopupHeader',
          `ChatPopupHeader--${size}`,
          size === 'collapsed' && newUnreadMessages > 0 && 'ChatPopupHeader--unreads',
          ['active', 'establishing'].includes(callStatus) && 'ChatPopupHeader--call',
        )}
        onClick={() => {
          setSize(s => (s === 'default' ? 'collapsed' : 'default'));
        }}
      >
        <div className="ChatPopupHeader__avatar">
          <VisitorAvatar visitor={visitor} size={28} />
          <div className="ChatPopupHeader__avatarBubble">
            <StatusBubble client={activeVisitor} />
          </div>
        </div>
        <div className="ChatPopupHeader__info">
          <div className="ChatPopupHeader__name">
            <div className="ChatPopupHeader__nameText">
              {size === 'collapsed' && visitor.displayName === 'Unknown'
                ? collapsedLine
                : firstLine}
            </div>
            {size === 'collapsed' && newUnreadMessages > 0 && (
              <span className={clsx('ChatPopupHeader__dot')}>
                {newUnreadMessages > 99 ? '99+' : newUnreadMessages}
              </span>
            )}
          </div>
          {secondLine && size !== 'collapsed' && (
            <div className="ChatPopupHeader__location">{secondLine}</div>
          )}
        </div>
        {['active', 'establishing'].includes(callStatus) && (
          <Icon
            icon={isReceivingAudio ? 'micSalesfloorOn' : 'micSalesfloorOff'}
            width={20}
            height={20}
            className="ChatPopupHeader__mic"
          />
        )}
        <div className="ChatPopupHeader__actions">
          {size !== 'collapsed' && (
            <>
              <SessionControlButton
                className="CallButton"
                menuClassName="CallButtons__menu"
                disabled={!activeVisitor}
                primary
                activeIcon={<CameraIcon />}
                icon={<CameraIcon />}
                text="Call"
                menuItems={menuItems}
              />
              <Tooltip title="View details">
                <Button
                  className="ChatPopupHeader__link"
                  type="ghost"
                  icon={<Icon icon="link" />}
                  to={`/visitor/${visitor.id}?cid=${conversationId}`}
                />
              </Tooltip>
            </>
          )}
          <Button
            className="ChatPopupHeader__close"
            type="ghost"
            icon={<Icon icon="delete" />}
            onClick={closeConversation}
          />
        </div>
      </div>
      <div
        className={clsx(
          'ChatPopupHeader__call',
          !['active', 'establishing'].includes(callStatus) &&
            'ChatPopupHeader__call--hidden',
        )}
      >
        <ChatPopupActiveCall visitor={visitor} />
      </div>
    </>
  );
};
